/**
 * Nova/baCurr currency layer — entry points, verbatim port of v1
 * nova_currency.js (research/06; REQ-CUR-1..6). No network I/O; reactive
 * triggers only: the sessionStorage.setItem monkey-patch (re-wrapping on
 * every run — nestable) and the MutationObserver (./observer.ts). The gate
 * lives in ./gate.ts, the converter factory in ./converter.ts, the formats
 * table in ./formats.ts (SPEC §2.1/§4.8; split in Phase 6 from the Phase-2
 * single-file port — this file keeps the shared 200 ms debounced formatter
 * and the ticker formatting passes).
 *
 * bare $ = BUNDLED jQuery; bacurr$ is Nova's page global (read via window).
 */
import $ from "jquery";
import { IsNovaCurrencyCompatible } from "./gate";
import { CurrencyConverter } from "./converter";
import { initNovaCurrencyMutationObserver } from "./observer";

export function RunNovaCurrencyConversionLogic(): void {
    if (IsNovaCurrencyCompatible()) {
        DebouncedFormatCurrencyWithAmount(null);
        SessionStorageTrigger();
         if (!window.__novaCurrencyObserverAttached) {
      initNovaCurrencyMutationObserver(DebouncedFormatCurrencyWithAmount);     // watch DOM changes
    }
    }
}

function Debounce(func: any, delay: any): (...args: any[]) => void {
    let timer: any;
    return function (this: any, ...args: any[]) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
    };
}

// The merchant-configurable <suffix> sibling lives outside the ticker span
// (build_elements/price-marker), so ticker.html() never touches it — left
// alone it goes stale the moment the shopper picks a currency other than the
// one it was typed for. Since the ticker itself now carries the code, clear
// it rather than leave it contradicting the amount.
function ClearTickerSuffix(ticker: any): void {
    ticker.next("suffix").empty();
}

export function HasPriceTickerFormattedWithNovaCurrency(amount: any, element: any): boolean {
    if (IsNovaCurrencyCompatible() && $(element).hasClass("cpc-priceTicker")) {
        const config = window.baCurr.config;
        const converter = CurrencyConverter(window.baCurr.Currency.rates);
        const targetCurrency = GetTargetCurrency(null);
        const ticker = window.bacurr$(element);
        if (ticker.length) {
            const originalValue = ticker.attr("data-origMoney");
            ticker.data("bacurr-origMoneyVal", originalValue);
            let result = converter.convert(parseFloat(amount), config.user_curr, targetCurrency, true);
            console.log(`Updating HTML with ${result} from HasPriceTicker`);
            ticker.html(result);
            ClearTickerSuffix(ticker);
            return true;
        }
    }
    return false;
}

function FormatPriceTickerCurrencyWithAmount(value: any): void {
    const config = window.baCurr.config;
    const converter = CurrencyConverter(window.baCurr.Currency.rates);
    const targetCurrency = GetTargetCurrency(value);
    $("span.money.cpc-priceTicker").each(function (this: any) {
        const convertTicker = () => {
            let priceTickerId = $(this).attr("id");
            const ticker = window.bacurr$("#" + priceTickerId);
            if (ticker.length) {
                const originalValue = ticker.attr("data-origMoney");
                ticker.data("bacurr-origMoneyVal", originalValue);
                const result = converter.convert(parseFloat(originalValue), config.user_curr, targetCurrency, true);
                console.log(`Updating HTML with ${result}`);
                ticker.html(result);
                ClearTickerSuffix(ticker);
            }
        };
        convertTicker();
    });
}

export function GetAmountAfterConversion(amountToConvert: any, userCurrency: any, targetCurrencyValue: any): any {
    const converter = CurrencyConverter(window.baCurr.Currency.rates);
    const targetCurrency = GetTargetCurrency(targetCurrencyValue);
    const result = converter.getConvertedAmount(parseFloat(amountToConvert), userCurrency, targetCurrency);
    return result;
}


// Create a debounced version of FormatCurrencyWithAmount with a 200ms delay
const DebouncedFormatCurrencyWithAmount = Debounce(FormatPriceTickerCurrencyWithAmount, 200);

export const GetTargetCurrency = (inputValue: any): any => {
    const sessionValue = sessionStorage.getItem("bacurr_user_cur");
    try {
        return inputValue ? JSON.parse(inputValue) : JSON.parse(sessionValue ?? (null as any));
    } catch {
        return JSON.parse(sessionValue ?? (null as any));
    }
};

function SessionStorageTrigger(): void {
    const originalSet = sessionStorage.setItem;
    // Override sessionStorage setItem
    sessionStorage.setItem = function (key: any, value: any) {
    if (key === "bacurr_user_cur") {
      // wait a tick so baCurr.config & rates are definitely updated
      requestAnimationFrame(() => DebouncedFormatCurrencyWithAmount(value));
    }
    return originalSet.apply(this, arguments as any);
  };

}
